import { useNavigation } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import ChipCardItem from 'components/services/ChipCardItem';
import CircleImageWrapper from 'components/services/CircleImageWrapper';
import { LoyaltyLogo } from 'components/services/LoyaltyLogo';
import ServiceImage from 'components/services/ServiceImage';
import { format, isAfter } from 'date-fns';
import { camelCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import {
  Maybe,
  PaymentStatus,
  UnifiedPermit,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import { testIdentifiers } from 'utils/testIdentifiers';
import {
  getPermitTitle,
  handlePermitNavigation,
  isFailedRecurringPayment,
  isPermitMissingVehicle,
  isPermitWaitingAction,
  showLicensePlate,
} from '../../utils/permitUtils';

export type MyPermitCardOptions = {
  showLabel?: boolean;
};

type PermitDateProps = {
  startingAt: Maybe<string>;
  endingAt: Maybe<string>;
  orderLines?: Maybe<UnifiedPermit['orderLines']>;
  isWaitingActivation: boolean;
  paymentState?: Maybe<PaymentStatus>;
};

const PermitCardServiceImage = ({
  id,
  isTurkuPmc,
  isWaitingActivation,
}: {
  id: string;
  isTurkuPmc?: Maybe<boolean>;
  isWaitingActivation: boolean;
}) => (
  <ServiceImage
    {...testIdentifiers(`${id}-permit-image`)}
    turkuPmc={isTurkuPmc || false}
    showGrayedOutIcon={isWaitingActivation}
  />
);

export const PermitCardLeftComponent = ({
  id,
  isTurkuPmc,
  isWaitingActivation,
  logo,
  permitType,
}: {
  id: string;
  isTurkuPmc?: Maybe<boolean>;
  isWaitingActivation: boolean;
  logo?: Maybe<string>;
  permitType?: Maybe<string>;
}) => {
  const ImageComponent = useCallback(
    () => (
      <PermitCardServiceImage
        id={id}
        isTurkuPmc={isTurkuPmc}
        isWaitingActivation={isWaitingActivation}
      />
    ),
    [id, isTurkuPmc, isWaitingActivation]
  );
  return permitType === 'LOYALTY' && !!logo ? (
    <LoyaltyLogo uri={logo} />
  ) : (
    <CircleImageWrapper
      backgroundColor={
        permitType === 'ACCESS' ||
        permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
        permitType === 'DIGITAL_PERMIT'
          ? theme.colors.lightBg.lilac
          : theme.colors.lightBg.green
      }
      showBorder={isWaitingActivation}
      testIdentifiersPrefix={`${id}-permit-`}
      ImageComponent={ImageComponent}
    />
  );
};
const MyPermitCard = (permit: UnifiedPermit, options?: MyPermitCardOptions) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const {
    id,
    orderLineIdWithVersion,
    isTurkuPmc,
    isWaitingActivation,
    orderLines,
    permitType,
    paymentState,
    orderStatus,
  } = permit;

  const title = useMemo(() => {
    return getPermitTitle(permit);
  }, [permit]);

  const getDateBasedText = (
    startingAt: Maybe<string>,
    endingAt: Maybe<string>
  ): string | null => {
    if (startingAt) {
      return t('permit.starts') + ' ' + startingAt;
    }
    if (endingAt) {
      return t('permit.ends') + ' ' + endingAt;
    }
    return null;
  };

  const getBillingBasedText = (
    billingFrequency: string | null | undefined,
    isWaitingActivation: boolean
  ): string => {
    const localizationKey = isWaitingActivation
      ? 'activatePermit'
      : `type.${billingFrequency}`;

    return t(`permit.${localizationKey}`);
  };

  const formatQuantityText = (
    quantity: number | null | undefined,
    text: string
  ): string => {
    return quantity ? `${quantity} \u00D7 ${text}` : text;
  };

  const startEndText = ({
    startingAt,
    endingAt,
    orderLines,
    isWaitingActivation,
  }: PermitDateProps): string => {
    if (isFailedRecurringPayment(permit)) {
      return t('permit.paymentFailed');
    }

    const dateText = getDateBasedText(startingAt, endingAt);
    if (dateText) {
      return dateText;
    }

    const billingFrequency = orderLines?.[0]?.billingFrequency;
    const quantity = orderLines?.[0]?.quantity;

    const baseText = getBillingBasedText(billingFrequency, isWaitingActivation);
    return formatQuantityText(quantity, baseText);
  };

  const salesOrderSubTitle = (
    orderLines: Maybe<UnifiedPermit['orderLines']>,
    paymentState: Maybe<PaymentStatus> | undefined
  ) => {
    let startingAt: Maybe<string> = null;
    let endingAt: Maybe<string> = null;
    const validFrom = orderLines?.[0]?.validity?.validFrom;
    if (validFrom) {
      const validFromDate = new Date(validFrom);
      if (
        !Number.isNaN(validFromDate.getTime()) &&
        isAfter(validFromDate, new Date())
      ) {
        startingAt = format(validFromDate, 'd.M.y');
      }
    }
    const validTo = orderLines?.[0]?.validity?.validTo;
    if (validTo) {
      const validToDate = new Date(validTo);
      if (!Number.isNaN(validToDate.getTime())) {
        endingAt = format(validToDate, 'd.M.y');
      }
    }
    return orderLines?.[0]?.billingType === 'ONE_TIME'
      ? ''
      : startEndText({
          startingAt,
          endingAt,
          orderLines,
          isWaitingActivation,
          paymentState,
        });
  };

  const digitalPermitSubTitle = () => {
    let startingAt: Maybe<string> = null;
    let endingAt: Maybe<string> = null;
    const validFrom = permit.validity?.validFrom;
    if (validFrom) {
      const validFromDate = new Date(validFrom);
      if (
        !Number.isNaN(validFromDate.getTime()) &&
        isAfter(validFromDate, new Date())
      ) {
        const startDate = permit.startDate ? new Date(permit.startDate) : null;
        if (startDate && !Number.isNaN(startDate.getTime())) {
          startingAt = format(startDate, 'd.M.y HH:mm');
        }
      }
    }
    const validTo = permit.validity?.validTo;
    if (validTo) {
      const validToDate = new Date(validTo);
      if (!Number.isNaN(validToDate.getTime())) {
        endingAt = format(validToDate, 'd.M.y HH:mm');
      }
    }
    return getDateBasedText(startingAt, endingAt);
  };

  const subTitle = useMemo(() => {
    if (permitType === 'SALES_ORDER') {
      return salesOrderSubTitle(orderLines, paymentState);
    }

    if (permitType === 'DIGITAL_PERMIT') {
      return digitalPermitSubTitle();
    }

    if (permitType === 'LOYALTY') {
      return undefined;
    }

    if (permitType === 'WAITING_LIST') {
      return orderStatus === 'WAITING_FOR_CSR_APPROVAL'
        ? t('longTermParking.waitingList.inReview')
        : t('longTermParking.waitingList.onWaitingList');
    }

    if (
      showLicensePlate(permit) &&
      isPermitMissingVehicle(permit?.registrationPlates)
    ) {
      return t('permit.addVehicle');
    }

    return t(`permit.${camelCase(permitType?.toString())}.name`, '');
  }, [permitType, isWaitingActivation, orderLines, t, permit]);

  const additionalTitle = useMemo(() => {
    if (
      (permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
        permitType === 'DIGITAL_PERMIT') &&
      permit.pmcName
    ) {
      return permit.pmcName;
    }

    if (
      permitType === 'ACCESS' &&
      permit.accessPermitPoolingGroupInfo?.length === 1
    ) {
      return permit.accessPermitPoolingGroupInfo[0]?.pmcName || undefined;
    }

    return undefined;
  }, [permitType, permit]);

  const handleNavigation = () => {
    usePermitStore
      .getState()
      .setSelectedPermit(orderLineIdWithVersion ?? permit.id);
    if (permitType === 'SALES_ORDER') {
      return navigate('salesOrderPermitDetails', {
        orderLineIdWithVersion: orderLineIdWithVersion ?? '',
      });
    }
    if (permitType === 'WAITING_LIST') {
      return navigate('longTermParking', {
        screen: 'waitingListPermitDetails',
        params: {
          id: permit.id,
        },
      });
    }
    handlePermitNavigation(permit, navigate);
  };

  const LeftComponent = useCallback(() => {
    return (
      <PermitCardLeftComponent
        isWaitingActivation={
          isWaitingActivation || permitType === 'WAITING_LIST'
        }
        isTurkuPmc={isTurkuPmc}
        id={id}
        logo={permit?.logo}
        permitType={permit?.permitType}
      />
    );
  }, [
    id,
    isTurkuPmc,
    isWaitingActivation,
    permit?.logo,
    permit?.permitType,
    permitType,
  ]);

  return (
    <ChipCardItem
      title={title ?? 'Permit'}
      subTitle={subTitle ?? undefined}
      additionalTitle={additionalTitle}
      LeftComponent={LeftComponent}
      onPress={handleNavigation}
      {...testIdentifiers('my-permit-chip-card')}
      isWaitingAction={isPermitWaitingAction(permit)}
    />
  );
};

export default MyPermitCard;
